import { useMemo } from 'react';
import { AssortmentItemMinimal, CentralAssortmentItem } from '@retail/my-assortment/types';
import { useMyAssortmentTranslation } from '@retail/my-assortment/i18n';
import { GridColDef } from '@mui/x-data-grid-premium';
import { ExternalItemSource } from '@retail/products/components';
import { SupplierItemAssortmentTag } from '@retail/products/types';
import { AssortmentTableActions } from '../AssortmentTable/AssortmentTableActions';
import { useItemInfoColumns } from '../AssortmentTable';
import { AssortmentTagsCell } from './AssortmentTagsCell';
import { OrgUnitType } from '@retail/mgpris/config-types';

type GridColAssortmentItem = GridColDef<CentralAssortmentItem>;

export interface ShowColumnProps {
  orgUnitType: OrgUnitType;
}
interface Props {
  deleteItem?: (itemId: number) => void;
  addItem?: (items: AssortmentItemMinimal) => void;
  deleteAssortmentItemTag?: (tag: SupplierItemAssortmentTag, mgSupplierItemId: number) => void;
  selectSupplier: (supplierId: number) => void;
  selectProductGroup: (groupNumber: string) => void;
  selectRowId?: (rowId: number) => void;
  externalItemSource: ExternalItemSource;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
  sortable: false,
};

export const useCentralAssortmentColumns = ({
  deleteItem,
  addItem,
  deleteAssortmentItemTag,
  selectSupplier,
  selectProductGroup,
  selectRowId,
  externalItemSource,
}: Props): GridColAssortmentItem[] => {
  const { t } = useMyAssortmentTranslation();

  const {
    itemIdsColumn,
    itemNameColumn,
    supplierColumn,
    productGroupColumn,
    itemStatusColumn,
    supplierItemPriorityColumn,
    discontinuationColumn,
  } = useItemInfoColumns<CentralAssortmentItem>({
    selectSupplier,
    selectProductGroup,
    externalItemSource,
  });

  return useMemo(
    () => [
      itemStatusColumn,
      itemIdsColumn,
      itemNameColumn,
      supplierItemPriorityColumn,
      supplierColumn,
      productGroupColumn,
      {
        ...baseColumnProps,
        width: 220,
        field: 'tagName',
        headerName: t('myAssortment.columns.subAssortments'),
        renderCell: ({ row }) => (
          <AssortmentTagsCell
            assortmentTags={row.assortmentTags}
            deleteTag={
              deleteAssortmentItemTag
                ? (tag) => deleteAssortmentItemTag(tag, row.mgSupplierItemId)
                : undefined
            }
          />
        ),
      },
      discontinuationColumn,
      {
        ...baseColumnProps,
        field: 'addedBy',
        headerName: t('myAssortment.columns.addedBy'),
        renderCell: ({ row }) =>
          row.createdBy ? `${row.createdBy.givenName} ${row.createdBy.familyName}` : '-',
      },
      {
        ...baseColumnProps,
        width: 180,
        field: 'mainSupplierName',
        headerName: t('myAssortment.columns.mainSupplierName'),
        renderCell: ({ row }) => row.mainSupplierName ?? '-',
      },
      {
        ...baseColumnProps,
        field: 'actions',
        width: 85,
        sortable: false,
        hideable: false,
        headerAlign: 'center',
        align: 'center',
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <AssortmentTableActions
            viewItemProductCard={selectRowId ? () => selectRowId(row.mgSupplierItemId) : undefined}
            addItemToAssortment={
              addItem && !row.inAssortment ? () => addItem({ ...row }) : undefined
            }
            deleteItem={deleteItem ? () => deleteItem(row.mgSupplierItemId) : undefined}
          />
        ),
      },
    ],
    [
      itemStatusColumn,
      itemIdsColumn,
      itemNameColumn,
      supplierItemPriorityColumn,
      supplierColumn,
      productGroupColumn,
      t,
      discontinuationColumn,
      deleteAssortmentItemTag,
      selectRowId,
      deleteItem,
      addItem,
    ]
  );
};
